import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getGetAutoQuoteExamplesQueryKey, useGetAutoQuoteExamples, type AutoQuoteExample } from "@workspace/api-client-react";
import { AlertCircle, ListChecks, Pause, Play, RotateCw, Timer } from "lucide-react";
import "./quote-example-scroller.css";

export type QuoteExample = AutoQuoteExample;

const MIN_GROUP_ROWS = 6;
const SECONDS_PER_ROW = 3.2;

/** Only eligible measured durations; never round a faster run up to qualify. */
export function formatGenerationTime(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 180 || seconds > 3600) return null;
  return `${Number((seconds / 60).toFixed(1))} min`;
}

/** Keep only rows with a genuine project, item count and a measured duration whose scope matches its source. */
export function displayableExamples(examples: QuoteExample[]): QuoteExample[] {
  return examples.filter((ex) => {
    const secs = ex.generationSeconds;
    const timed = typeof secs === "number" && Number.isFinite(secs) && secs >= 180 && secs <= 3600;
    const scopeOk = (ex.source === "benchmark" && ex.timingScope === "description_and_pricing") || (ex.source === "customer" && ex.timingScope === "pricing");
    return timed && scopeOk && !!ex.projectType?.trim() && Number.isInteger(ex.workItemCount) && ex.workItemCount > 0;
  });
}

export function timingScopeLabel(ex: Pick<QuoteExample, "timingScope">): string | null {
  if (ex.timingScope === "description_and_pricing") return "Job description and pricing";
  if (ex.timingScope === "pricing") return "Pricing only";
  return null;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

const METHOD_LABEL: Record<QuoteExample["quotingMethod"], string> = { auto: "Auto Quote", manual: "Manual Quote", template: "Template Quote" };

export function quotingMethodLabel(method: QuoteExample["quotingMethod"]): string | null {
  return METHOD_LABEL[method] ?? null;
}

export function exampleDisclosure(examples: Pick<QuoteExample, "source">[]): string {
  const bench = examples.filter((ex) => ex.source === "benchmark").length;
  if (bench === 0) return "Example identities";
  if (bench === examples.length) return "Example identities · Benchmark timings";
  return "Example identities · Includes benchmark timings";
}

function timeTitle(ex: QuoteExample): string {
  return ex.source === "benchmark" ? "Software test run: job description and pricing" : "Customer quote: pricing only";
}

function Row({ ex, testId }: { ex: QuoteExample; testId?: string }) {
  const time = formatGenerationTime(ex.generationSeconds) ?? "";
  const title = timeTitle(ex);
  const method = quotingMethodLabel(ex.quotingMethod);
  return (
    <div className="qes-row flex flex-col justify-center gap-0.5 sm:flex-row sm:items-center sm:justify-start sm:gap-3 min-w-0" data-testid={testId} data-source={ex.source}>
      <span className="min-w-0 truncate text-sm leading-tight sm:max-w-[40%] sm:shrink-0">
        <span className="font-semibold text-white" data-testid="text-example-name">{ex.displayName}</span>
        <span className="text-white/50">, </span>
        <span className="text-white/70" data-testid="text-example-company">{ex.companyName}</span>
      </span>
      <span className="flex min-w-0 flex-wrap sm:flex-nowrap sm:flex-1 items-center gap-1.5">
        <span className="min-w-0 basis-full sm:basis-auto sm:flex-1 truncate text-xs sm:text-sm text-[hsl(184_70%_82%)]" data-testid="text-example-project">{ex.projectType}</span>
        {method && (
          <span className="shrink-0 inline-flex items-center rounded border border-[hsl(184_70%_70%/0.45)] px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-[hsl(184_70%_78%)]" data-testid="badge-example-method">{method}</span>
        )}
        <span className="shrink-0 inline-flex items-center gap-1 rounded-md border border-white/20 px-1.5 py-px text-xs font-semibold tabular-nums text-white" data-testid="text-example-items">
          <ListChecks className="h-3 w-3" aria-hidden="true" />
          {ex.workItemCount} {ex.workItemCount === 1 ? "item" : "items"}
        </span>
        <span className="qes-time shrink-0 inline-flex items-center justify-end gap-1 rounded-md bg-[hsl(186_62%_42%/0.25)] px-1.5 py-px text-xs font-semibold tabular-nums text-[hsl(184_70%_82%)]" data-testid="text-example-time" title={title} aria-label={`${time}, ${title.toLowerCase()}`}>
          <Timer className="h-3 w-3" aria-hidden="true" />{time}
        </span>
      </span>
    </div>
  );
}

export function QuoteExampleList({ examples }: { examples: QuoteExample[] }) {
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reduced = usePrefersReducedMotion();
  // Apply refreshes at the loop boundary, not midway through the list.
  // A 60-second refetch must not prevent the final rows of a longer sequence
  // from ever being reached. Reduced-motion users receive updates immediately.
  const [sequence, setSequence] = useState(examples);
  const pending = useRef(examples);
  useEffect(() => {
    pending.current = examples;
    if (reduced || sequence.length < 2) setSequence(examples);
  }, [examples, reduced, sequence.length]);
  const animate = !reduced && sequence.length >= 2;

  const group = useMemo(() => {
    if (!animate) return [];
    const reps = Math.max(1, Math.ceil(MIN_GROUP_ROWS / sequence.length));
    return Array.from({ length: reps }, () => sequence).flat();
  }, [animate, sequence]);

  const list = (
    <ul aria-label="Selected quote examples" className={animate ? "sr-only" : "list-none m-0 p-0 max-h-[12rem] sm:max-h-[8.25rem] overflow-y-auto"} data-testid="list-quote-examples">
      {sequence.map((ex) => (
        <li key={ex.id}><Row ex={ex} testId={`row-quote-example-${ex.id}`} /></li>
      ))}
    </ul>
  );

  return (
    <div className="qes-root flex items-center gap-3 min-w-0" data-paused={paused || hovered || focused ? "true" : "false"} data-manual-paused={paused ? "true" : "false"} data-testid="quote-example-scroller" data-animated={animate ? "true" : "false"}>
      <div
        className="qes-panel min-w-0 flex-1"
        data-testid="quote-example-panel"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false); }}
      >
        {animate ? (
          <>
            {list}
            <div className="qes-viewport" aria-hidden="true" data-testid="quote-example-viewport">
              <div key={sequence.map(ex => ex.id).join(",")} className="qes-track" inert data-testid="quote-example-track" onAnimationIteration={() => setSequence(pending.current)} style={{ ["--qes-duration" as string]: `${(group.length * SECONDS_PER_ROW).toFixed(1)}s` }}>
                {[0, 1].map((copy) => group.map((ex, i) => <Row key={`${copy}-${i}-${ex.id}`} ex={ex} />))}
              </div>
            </div>
          </>
        ) : list}
      </div>
      {animate && (
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-pressed={paused}
          aria-label={paused ? "Resume scrolling" : "Pause scrolling"}
          className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/25 text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(184_70%_70%)]"
          data-testid="button-toggle-quote-examples"
        >
          {paused ? <Play className="h-3.5 w-3.5" aria-hidden="true" /> : <Pause className="h-3.5 w-3.5" aria-hidden="true" />}
        </button>
      )}
    </div>
  );
}

export function QuoteExampleScroller() {
  const { data, isLoading, isError, refetch, isFetching } = useGetAutoQuoteExamples({
    query: { queryKey: getGetAutoQuoteExamplesQueryKey(), staleTime: 15_000, refetchInterval: 60_000, refetchIntervalInBackground: false, refetchOnWindowFocus: true, retry: 1 },
  });
  const raw = useMemo<QuoteExample[]>(() => data?.examples ?? [], [data]);
  const examples = useMemo(() => displayableExamples(raw), [raw]);

  const strip = (children: ReactNode, labelled = false) => (
    <section className="qes-strip w-full" aria-labelledby={labelled ? "quote-examples-heading" : undefined} aria-label={labelled ? undefined : "Builder community"} data-testid="section-quote-examples">
      <div className="w-full px-4 sm:px-6 lg:px-10 py-2.5 flex flex-col md:flex-row md:items-center gap-1 md:gap-6">{children}</div>
    </section>
  );
  const proof = (
    <p className="text-sm text-white/80" data-testid="status-quote-examples-empty">
      <span className="font-semibold text-white">Join hundreds of registered users.</span> See how quickly you can quote your next job.
    </p>
  );

  if (isLoading && !data) {
    return strip(<div className="h-6 w-full rounded bg-white/10 animate-pulse" data-testid="status-quote-examples-loading" aria-busy="true" aria-label="Loading quote examples" />);
  }
  if (isError && !data) {
    return strip(
      <div className="flex w-full items-center gap-3 text-sm text-white/80" role="alert" data-testid="status-quote-examples-error">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1">Quote examples could not load.</span>
        <button type="button" onClick={() => refetch()} disabled={isFetching} className="inline-flex items-center gap-1 rounded-md border border-white/25 px-2 py-1 text-xs font-semibold hover:bg-white/10" data-testid="button-retry-quote-examples">
          <RotateCw className="h-3 w-3" aria-hidden="true" />Try again
        </button>
      </div>,
    );
  }
  if (examples.length === 0) return strip(proof);

  return strip(
    <>
      {isError && <span role="status" className="sr-only">Could not refresh quote examples. Showing previously loaded examples.</span>}
      <div className="flex flex-col self-stretch shrink-0 py-1 md:pt-4 md:max-w-[19rem]">
        <h2 id="quote-examples-heading" className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-[hsl(184_70%_72%)]">Selected quote examples</h2>
        <p className="text-xs leading-snug text-white/80" data-testid="text-quote-examples-invite">See what other builders are quoting for. Join hundreds of registered users and quote your job.</p>
        <p className="mt-4 pt-1 text-[11px] leading-relaxed text-white/70 md:mt-auto" data-testid="text-quote-examples-note">{exampleDisclosure(examples)}</p>
      </div>
      <div className="min-w-0 flex-1"><QuoteExampleList examples={examples} /></div>
    </>,
    true,
  );
}
